/**
 * KAN-835: блок "Уведомления" во вкладке "Профиль" приложения менеджера:
 * уведомления на это устройство (Web Push) и свой Telegram в боте для
 * менеджеров. Логика в usePushSubscription и useMyTelegramLink.
 * KAN-845: PushControls и TelegramControls переиспользует экран "Начало работы".
 */
import { BellRing, CheckCircle2, Loader2, Send } from 'lucide-react';
import { useMyNotificationsStatus, usePushSubscription } from '@/hooks/usePushSubscription';
import { useMyTelegramLink } from '@/hooks/useMyTelegramLink';
import { PUSH_TEXTS, TELEGRAM_TEXTS } from '@/lib/managerNotificationsTexts';
import type { ManagerNotificationsStatus } from '@/services/api/managerNotifications';

const BUTTON =
  'flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-medium disabled:opacity-60';

type PushState = ReturnType<typeof usePushSubscription>;

/**
 * KAN-845: кнопки и состояния Web Push без заголовка; их же показывает экран
 * "Начало работы". iosHint заменяет текст для iPhone без установки.
 */
export function PushControls({ push, iosHint }: { push: PushState; iosHint?: string }): JSX.Element {
  return (
    <>
      {push.view === 'loading' && <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-label="Загрузка" />}
      {push.view === 'ios-install' && <p className="text-sm text-gray-700">{iosHint ?? PUSH_TEXTS.iosInstall}</p>}
      {push.view === 'unsupported' && <p className="text-sm text-gray-700">{PUSH_TEXTS.unsupported}</p>}
      {push.view === 'denied' && <p className="text-sm text-amber-700">{PUSH_TEXTS.denied}</p>}
      {push.view === 'off' && (
        <>
          <p className="text-xs text-gray-500">{PUSH_TEXTS.hint}</p>
          <button
            type="button"
            onClick={() => void push.enable()}
            disabled={push.busy}
            className={`${BUTTON} bg-blue-600 text-white active:bg-blue-700`}
          >
            {push.busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {PUSH_TEXTS.enable}
          </button>
          <p className="text-xs text-gray-600" data-testid="push-vpn-note">
            {PUSH_TEXTS.vpnNote}
          </p>
        </>
      )}
      {push.view === 'on' && (
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-sm text-green-700" data-testid="push-on">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {PUSH_TEXTS.on}
          </span>
          <button
            type="button"
            onClick={() => void push.disable()}
            disabled={push.busy}
            className="h-11 rounded-xl border border-gray-300 px-4 text-sm text-gray-700 disabled:opacity-60"
          >
            {PUSH_TEXTS.disable}
          </button>
        </div>
      )}
      {push.error && (
        <p role="alert" className="text-sm text-red-600">
          {push.error}
        </p>
      )}
    </>
  );
}

function PushSection(): JSX.Element {
  const push = usePushSubscription();
  return (
    <div className="space-y-2" data-testid="push-section">
      <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <BellRing className="h-4 w-4" aria-hidden="true" />
        На этот телефон
      </p>
      <PushControls push={push} />
    </div>
  );
}

/** KAN-845: подключение своего Telegram без заголовка; его же показывает экран "Начало работы". */
export function TelegramControls({ status }: { status: ManagerNotificationsStatus }): JSX.Element {
  const telegram = useMyTelegramLink();
  return (
    <>
      {status.telegram_linked ? (
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-sm text-green-700" data-testid="telegram-linked">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {TELEGRAM_TEXTS.linked}
          </span>
          <button
            type="button"
            onClick={() => void telegram.disconnect()}
            disabled={telegram.busy}
            className="h-11 rounded-xl border border-gray-300 px-4 text-sm text-gray-700 disabled:opacity-60"
          >
            {TELEGRAM_TEXTS.disconnect}
          </button>
        </div>
      ) : status.bot_connected ? (
        <>
          <p className="text-xs text-gray-500">{TELEGRAM_TEXTS.hint}</p>
          <button
            type="button"
            onClick={() => void telegram.connect()}
            disabled={telegram.busy}
            className={`${BUTTON} border border-sky-500 text-sky-700 active:bg-sky-50`}
          >
            {telegram.busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {TELEGRAM_TEXTS.connect}
          </button>
        </>
      ) : (
        <p className="text-sm text-gray-500">{TELEGRAM_TEXTS.botMissing}</p>
      )}
      {telegram.error && (
        <p role="alert" className="text-sm text-red-600">
          {telegram.error}
        </p>
      )}
    </>
  );
}

function TelegramSection(): JSX.Element | null {
  const { data } = useMyNotificationsStatus();
  if (!data) return null;
  return (
    <div className="space-y-2 border-t border-gray-100 pt-4" data-testid="telegram-section">
      <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <Send className="h-4 w-4" aria-hidden="true" />
        Telegram
      </p>
      <TelegramControls status={data} />
    </div>
  );
}

export function MobileNotificationsBlock(): JSX.Element | null {
  const { hasManager, isError } = useMyNotificationsStatus();
  if (!hasManager) return null;
  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-4" data-testid="notifications-block">
      <h2 className="text-base font-semibold text-gray-900">Уведомления</h2>
      {isError ? (
        <p className="text-sm text-red-600">Не удалось загрузить настройки уведомлений</p>
      ) : (
        <>
          <PushSection />
          <TelegramSection />
        </>
      )}
    </section>
  );
}
